/**
 * Product Scanner Component
 * Tab 2: Barcode scanning and product lookup
 * Displays allergen match with safety classification (RED/YELLOW/GREEN)
 */

import React, { useState } from 'react';
import { Barcode, Camera, Info } from 'lucide-react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import Alert from '../common/Alert';
import ReactionForm from '../Reactions/ReactionForm';
import useAPI from '../../hooks/useAPI';

function ProductScanner() {
  const [barcode, setBarcode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [safetyCheck, setSafetyCheck] = useState(null);
  const [showHiddenNames, setShowHiddenNames] = useState(false);
  const [showReactionForm, setShowReactionForm] = useState(false);
  const [reactionLogged, setReactionLogged] = useState(false);

  const { post, get } = useAPI();

  /**
   * Scan barcode and check allergens
   */
  const handleScan = async (barcodeValue) => {
    if (!barcodeValue || !/^\d{8,14}$/.test(barcodeValue)) {
      setError('Invalid barcode format (must be 8-14 digits)');
      return;
    }

    setLoading(true);
    setError('');
    setProduct(null);
    setSafetyCheck(null);
    setShowReactionForm(false);
    setReactionLogged(false);

    try {
      const data = await post('/api/products/scan', { barcode: barcodeValue });
      setProduct(data.product);
      setSafetyCheck(data.safety_check);
      setBarcode('');
    } catch (err) {
      setError(err.message || 'Product not found. Try searching manually.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search product by name
   */
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError('Enter a search query');
      return;
    }

    setLoading(true);
    setError('');
    setProduct(null);
    setSafetyCheck(null);
    setShowReactionForm(false);
    setReactionLogged(false);

    try {
      const data = await get(`/api/products/search?q=${encodeURIComponent(searchQuery)}`);
      if (data.results.length === 0) {
        setError('No products found. Try a different search.');
      } else {
        // For MVP, just show first result
        setProduct(data.results[0].product);
        setSafetyCheck(data.results[0].safety_check);
      }
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Safety badge renderer
   */
  const SafetyBadge = ({ safetyLevel }) => {
    if (safetyLevel === 'RED') {
      return <Badge variant="danger" size="lg">🚫 UNSAFE</Badge>;
    }
    if (safetyLevel === 'YELLOW') {
      return <Badge variant="warning" size="lg">⚠️ CAUTION</Badge>;
    }
    return <Badge variant="success" size="lg">✅ APPEARS SAFE</Badge>;
  };

  return (
    <div className="scanner-container">
      <h1 className="page-title">Product Scanner</h1>

      {/* Barcode Input Section */}
      <Card className="input-card">
        <div className="input-section">
          <label className="input-label">Scan or Enter Barcode</label>
          <div className="barcode-input-group">
            <input
              type="text"
              inputMode="numeric"
              placeholder="Enter barcode (e.g., 5000112141419)"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value.replace(/\D/g, ''))}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleScan(barcode);
                }
              }}
              className="input input-lg"
              disabled={loading}
            />
            <button
              onClick={() => handleScan(barcode)}
              disabled={!barcode || loading}
              className="btn btn-primary"
            >
              <Barcode size={20} />
            </button>
            {/* Camera button placeholder - implement in Phase 1.5 */}
            <button className="btn btn-secondary" title="Camera not yet available (Phase 1.5)">
              <Camera size={20} />
            </button>
          </div>
        </div>

        {/* Search as alternative */}
        <form onSubmit={handleSearch} className="search-form">
          <label className="input-label">Or Search by Product Name</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="e.g., Skippy Peanut Butter"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input input-lg"
              disabled={loading}
            />
            <button type="submit" disabled={!searchQuery || loading} className="btn btn-primary">
              Search
            </button>
          </div>
        </form>
      </Card>

      {/* Error Alert */}
      {error && <Alert variant="error" title="Error" message={error} />}

      {/* Loading State */}
      {loading && (
        <Card className="loading-card">
          <p className="text-center">Scanning product...</p>
          <div className="loading-spinner" />
        </Card>
      )}

      {/* Product Result Section */}
      {product && safetyCheck && (
        <>
          {/* Safety Result */}
          <Card className={`safety-result safety-${safetyCheck.safety_level.toLowerCase()}`}>
            <div className="safety-header">
              <SafetyBadge safetyLevel={safetyCheck.safety_level} />
              <div className="safety-message">
                <p className="safety-text">{safetyCheck.recommendation}</p>
              </div>
            </div>

            {/* Product Info */}
            <div className="product-info">
              <h3 className="product-name">{product.name}</h3>
              <p className="product-brand">by {product.brand || 'Unknown Brand'}</p>
              <p className="product-barcode">Barcode: {product.barcode}</p>
            </div>

            {/* Matched Allergens */}
            {safetyCheck.matched_allergens && safetyCheck.matched_allergens.length > 0 && (
              <div className="matched-allergens">
                <p className="section-label">Matched to Your Allergies:</p>
                <div className="allergen-list">
                  {safetyCheck.matched_allergens.map((allergen) => (
                    <Badge key={allergen} variant="danger">
                      {allergen}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Allergen Details */}
            <div className="allergen-details">
              {safetyCheck.contains_allergen && (
                <Alert variant="error" title="Contains" message={`Product CONTAINS your allergen`} />
              )}
              {safetyCheck.may_contain_allergen && (
                <Alert variant="warning" title="May Contain" message={`Product MAY CONTAIN your allergen (manufacturer disclosure)`} />
              )}
              {safetyCheck.facility_processed && (
                <Alert variant="warning" title="Cross-Contact Risk" message={`Processed in facility with your allergen`} />
              )}
            </div>

            {/* Data Quality */}
            <div className="data-quality">
              <p className="data-age">
                <Info size={16} />
                Data updated {safetyCheck.data_age_hours} hours ago
              </p>
              <p className="confidence">
                Confidence: {Math.round(safetyCheck.confidence * 100)}%
                {safetyCheck.confidence < 0.7 && (
                  <span className="confidence-warning"> - Verify with manufacturer</span>
                )}
              </p>
              <p className="label-reminder">
                ⚠️ <strong>Always read the physical label</strong> before consuming. Ingredients change frequently.
              </p>
            </div>

            {/* Log Reaction */}
            {reactionLogged ? (
              <Alert
                variant="success"
                title="Reaction logged"
                message="Saved to your reaction history."
              />
            ) : showReactionForm ? (
              <ReactionForm
                product={{ id: product.id, name: product.name }}
                onLogged={() => {
                  setShowReactionForm(false);
                  setReactionLogged(true);
                }}
                onCancel={() => setShowReactionForm(false)}
              />
            ) : (
              <button
                className="btn btn-full btn-warning"
                onClick={() => setShowReactionForm(true)}
              >
                🚨 Report a Reaction to This Product
              </button>
            )}
          </Card>

          {/* Hidden Names Reference */}
          <Card className="hidden-names-card">
            <button
              onClick={() => setShowHiddenNames(!showHiddenNames)}
              className="btn-toggle"
            >
              <span>Hidden Allergen Names</span>
              <span className="toggle-arrow">{showHiddenNames ? '▼' : '▶'}</span>
            </button>

            {showHiddenNames && (
              <div className="hidden-names-content">
                <p className="section-label">
                  Allergens may be listed under scientific or alternative names:
                </p>
                <div className="names-grid">
                  <div className="name-item">
                    <strong>Milk</strong>
                    <p>casein, whey, lactalbumin, lactose</p>
                  </div>
                  <div className="name-item">
                    <strong>Eggs</strong>
                    <p>albumin, globulin, lysozyme, mayonnaise</p>
                  </div>
                  <div className="name-item">
                    <strong>Peanuts</strong>
                    <p>arachis, groundnut</p>
                  </div>
                  {/* Add more as needed */}
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Empty State */}
      {!product && !error && !loading && (
        <Card className="empty-state">
          <Barcode size={48} className="empty-icon" />
          <p className="empty-text">Scan a barcode or search for a product to check allergens</p>
        </Card>
      )}
    </div>
  );
}

export default ProductScanner;
